import React, { useState, useEffect, useRef } from 'react';
import { CheckCheck, Square, Shuffle, Layers, Clock, Wand2 } from 'lucide-react';
import { Level, LevelType } from '../../types/Level';
import firebaseService from '../../services/firebaseService';
import {
  claudeJsonWithSavedKey, claudeTextWithSavedKey, claudeVisionWithSavedKey, researchWithClaude,
  researchSourcesWithSavedKey, generateImage, describeAIError,
} from '../../ai';
import { getKey } from '../../ai/keys';
import { processAndUploadImage } from '../../utils/imageUpload';
import { deleteImage } from '../../services/storageService';
import { DraftLevel } from '../../studio/types';
import { runBatch } from '../../studio/pipeline';
import { Dropped, identityKey, candidateLabel } from '../../studio/rules';
import { loadQueue, saveQueue, loadRejected, addRejected } from '../../studio/queueStore';
import { publishDraft, canApprove, canBulkApprove, nextFreeId, draftToPrefill } from '../../studio/publish';
import { createSerialQueue } from '../../studio/serial';
import { buildGrid } from '../../studio/gridBuilder';
import {
  makePicture, autoPicture, isFatalPictureError, PictureServices, PictureStep, PICTURE_FLAG_PREFIX,
} from '../../studio/picturePipeline';
import { createJobQueue, JobQueue, JobQueueState, jobStateOf } from '../../studio/jobQueue';
import { makeFunFact, withFunFact, withoutFunFact, isFatalFactError, draftsMissingFact } from '../../studio/funFact';
import { fillGaps } from '../../studio/gaps';
import { TypeFilter, filterByType, countByType, groupByType, newestFirst, mixTypes } from '../../studio/queueOrder';
import { LEVEL_TYPE_LABELS, ORDERED_TYPES } from '../../studio/planner';
import OrderPanel from './OrderPanel';
import DraftCard from './DraftCard';
import ImageCropModal from './ImageCropModal';
import ImageSearchModal from './ImageSearchModal';
import { searchQueryFor } from '../../studio/imageSearch';
import './LevelStudio.css';

interface LevelStudioProps {
  visible: boolean;
  levels: Level[];
  onLevelCreated: (level: Level) => void;
  onEditDraft: (draft: DraftLevel, prefill: Partial<Level>) => void;
}

// The Studio reads and writes levels through the strict service functions only:
// a failed read must stop the action, and a write must never overwrite or fall back to local storage.
const publishDeps = {
  loadLevels: () => firebaseService.getLevelsFromServer(),
  createLevel: (id: string, data: Omit<Level, 'id'>) => firebaseService.createLevelIfAbsent(id, data),
};

const pictureServices: PictureServices = {
  research: async prompt => (await researchWithClaude(prompt)).text,
  text: prompt => claudeTextWithSavedKey(prompt),
  draw: prompt => generateImage(prompt),
  inspect: (image, prompt, schema) => claudeVisionWithSavedKey(image, prompt, schema),
  upload: (image, filename) => processAndUploadImage(image, filename, { autoReduce: true }),
};

const STEP_LABELS: Record<PictureStep, string> = {
  researching: 'Researching the song...',
  designing: 'Designing the scene...',
  drawing: 'Drawing the picture...',
  checking: 'Checking the picture...',
  saving: 'Saving the picture...',
};

const NO_GEMINI_KEY = 'Add a Gemini API key in Settings > API Keys to generate pictures.';

const LevelStudio: React.FC<LevelStudioProps> = ({ visible, levels, onLevelCreated, onEditDraft }) => {
  const [queue, setQueue] = useState<DraftLevel[]>(() => loadQueue());
  const [counts, setCounts] = useState<Partial<Record<LevelType, number>>>({});
  const [theme, setTheme] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dropped, setDropped] = useState<Dropped[]>([]);
  const [busy, setBusy] = useState<Record<string, string>>({});
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const [hasClaudeKey, setHasClaudeKey] = useState(false);
  const [hasGeminiKey, setHasGeminiKey] = useState(false);
  const [cropping, setCropping] = useState<{ draftId: string; file: Blob; credit: string | null } | null>(null);
  const [finding, setFinding] = useState<{ draftId: string; query: string } | null>(null);
  const [jobsState, setJobsState] = useState<JobQueueState>({ running: null, waiting: [] });
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [factJobsState, setFactJobsState] = useState<JobQueueState>({ running: null, waiting: [] });
  const [factErrors, setFactErrors] = useState<Record<string, string>>({});
  const [withFacts, setWithFacts] = useState(true);

  // Approvals run strictly one at a time so each one sees the number the previous one took
  const runApproval = useRef(createSerialQueue()).current;

  // The screen stays mounted while hidden: refresh what may have changed elsewhere each time it is shown
  useEffect(() => {
    if (!visible) return;
    setHasClaudeKey(getKey('claudeToken') !== '');
    setHasGeminiKey(getKey('geminiToken') !== '');
    setQueue(loadQueue());
  }, [visible]);

  const updateQueue = (change: (current: DraftLevel[]) => DraftLevel[]) => {
    const next = change(loadQueue());
    saveQueue(next);
    setQueue(next);
  };

  const setCardBusy = (id: string, action: string | null) =>
    setBusy(prev => {
      const next = { ...prev };
      if (action) next[id] = action; else delete next[id];
      return next;
    });

  const setCardError = (id: string, message: string | null) =>
    setCardErrors(prev => {
      const next = { ...prev };
      if (message) next[id] = message; else delete next[id];
      return next;
    });

  const setFactError = (id: string, message: string | null) =>
    setFactErrors(prev => {
      const next = { ...prev };
      if (message) next[id] = message; else delete next[id];
      return next;
    });

  // One picture job. Only this card is locked while it runs; errors go to the job controller.
  const runPictureJob = async (draftId: string) => {
    const draft = loadQueue().find(item => item.id === draftId);
    if (!draft) return;

    setCardError(draftId, null);
    setCardBusy(draftId, 'Starting...');
    try {
      const result = await makePicture(
        draft,
        pictureServices,
        step => setCardBusy(draftId, STEP_LABELS[step]),
        // Save the prompt the moment it exists, so a later failure never pays for the research again
        imagePrompt => updateQueue(current => current.map(item => (item.id === draftId ? { ...item, imagePrompt } : item)))
      );

      updateQueue(current => current.map(item => item.id === draftId
        ? {
            ...item,
            imagePrompt: result.imagePrompt,
            image: result.image,
            imageSize: result.imageSize,
            flags: [...item.flags.filter(flag => flag.indexOf(PICTURE_FLAG_PREFIX) !== 0), ...result.flags],
            status: item.status === 'needs-picture' ? 'ready' : item.status,
            imageCredit: undefined,
          }
        : item));
    } finally {
      setCardBusy(draftId, null);
    }
  };

  // Created once. The functions it calls only use state setters and browser storage, so they stay valid.
  const jobsRef = useRef<JobQueue | null>(null);
  if (!jobsRef.current) {
    jobsRef.current = createJobQueue({
      runJob: runPictureJob,
      onChange: setJobsState,
      onError: (draftId, err) => setCardError(draftId, describeAIError(err)),
      isFatal: isFatalPictureError,
      onSkipped: (draftIds, err) => draftIds.forEach(id =>
        setCardError(id, `Not started: picture generation is failing (${describeAIError(err)})`)),
    });
  }
  const jobs = jobsRef.current;

  // One fun fact job: Claude searches the web, and the source it names is checked against the pages it found
  const runFactJob = async (draftId: string) => {
    const draft = loadQueue().find(item => item.id === draftId);
    if (!draft) return;

    setFactError(draftId, null);
    const result = await makeFunFact(draft, prompt => researchSourcesWithSavedKey(prompt));
    updateQueue(current => current.map(item => (item.id === draftId ? withFunFact(item, result) : item)));
  };

  // Fun facts have their own line, so a card can get its fact and its picture at the same time
  const factJobsRef = useRef<JobQueue | null>(null);
  if (!factJobsRef.current) {
    factJobsRef.current = createJobQueue({
      runJob: runFactJob,
      onChange: setFactJobsState,
      onError: (draftId, err) => setFactError(draftId, describeAIError(err)),
      isFatal: isFatalFactError,
      onSkipped: (draftIds, err) => draftIds.forEach(id =>
        setFactError(id, `Not started: fun facts are failing (${describeAIError(err)})`)),
    });
  }
  const factJobs = factJobsRef.current;

  const writeFact = (draftId: string) => {
    setFactError(draftId, null);
    factJobs.enqueue(draftId);
  };

  const removeFact = (draftId: string) => {
    factJobs.cancel(draftId);
    setFactError(draftId, null);
    updateQueue(current => current.map(item => (item.id === draftId ? withoutFunFact(item) : item)));
  };

  const generatePicture = (draftId: string) => {
    if (getKey('geminiToken') === '') {
      setCardError(draftId, NO_GEMINI_KEY);
      return;
    }
    setCardError(draftId, null);
    jobs.enqueue(draftId);
  };

  const start = async () => {
    setRunning(true);
    setError(null);
    setNotice(null);
    setDropped([]);
    try {
      // Read the real level list first: without it the plan could repeat existing levels on a paid call
      const freshLevels = await firebaseService.getLevelsFromServer();
      const result = await runBatch(
        { counts, theme },
        { levels: freshLevels, rejected: loadRejected(), queued: loadQueue() },
        { planJson: (prompt, schema) => claudeJsonWithSavedKey(prompt, schema) }
      );
      updateQueue(current => [...result.drafts, ...current]);
      setDropped(result.dropped);

      // Songs and sayings get their picture drawn without being asked, when there is an image key to draw with
      const toDraw = result.drafts.filter(draft => autoPicture(draft.levelType));
      const canDraw = getKey('geminiToken') !== '';
      if (canDraw) toDraw.forEach(draft => jobs.enqueue(draft.id));

      // Every new level gets a fun fact found and checked on the web, unless that was switched off for this batch
      if (withFacts) result.drafts.forEach(draft => factJobs.enqueue(draft.id));

      setNotice(
        `${result.drafts.length} level${result.drafts.length === 1 ? '' : 's'} added to the queue.` +
        (result.shortfall > 0 ? ` ${result.shortfall} could not be filled; run another batch for more.` : '') +
        (toDraw.length > 0 && !canDraw ? ` Pictures were not started: ${NO_GEMINI_KEY}` : '')
      );
    } catch (err) {
      setError(describeAIError(err));
    } finally {
      setRunning(false);
    }
  };

  const approve = (draftId: string): Promise<void> => {
    setCardBusy(draftId, 'approve');
    setCardError(draftId, null);

    return runApproval(async () => {
      try {
        // Re-read the draft: it may have been rejected, edited or approved while waiting its turn
        const draft = loadQueue().find(item => item.id === draftId);
        if (!draft || !canApprove(draft)) return;

        const level = await publishDraft(draft, publishDeps);
        onLevelCreated(level);
        updateQueue(current => current.filter(item => item.id !== draftId));
      } catch (err) {
        setCardError(draftId, describeAIError(err));
      } finally {
        setCardBusy(draftId, null);
      }
    });
  };

  // Cards with a warning are left for a look and their own Approve click
  const typeCounts = countByType(queue);
  // Showing an empty type would hide the whole queue, so fall back to everything
  const activeFilter: TypeFilter = typeFilter !== 'all' && typeCounts[typeFilter] === 0 ? 'all' : typeFilter;
  const visibleQueue = filterByType(queue, activeFilter);
  // A card whose fun fact is still on its way is not finished, so "Approve all" leaves it for later
  const bulkReady = visibleQueue.filter(draft =>
    canBulkApprove(draft) && !busy[draft.id] && jobStateOf(factJobsState, draft.id) === 'idle');
  const missingFacts = draftsMissingFact(visibleQueue, factJobsState, busy);

  const approveAll = () => {
    bulkReady.forEach(draft => {
      jobs.cancel(draft.id);
      approve(draft.id);
    });
  };

  const reject = (draft: DraftLevel) => {
    jobs.cancel(draft.id);
    factJobs.cancel(draft.id);
    setFactError(draft.id, null);
    addRejected({ key: identityKey(draft), label: candidateLabel(draft) });
    if (draft.image) {
      deleteImage(draft.image).catch(err => console.warn('Could not delete the rejected picture:', err));
    }
    setCardError(draft.id, null);
    updateQueue(current => current.filter(item => item.id !== draft.id));
  };

  // Receives the square picture from the crop popup; the upload compresses it to 200 KB or less
  const attachPicture = async (draftId: string, picture: Blob, credit: string | null) => {
    const draft = loadQueue().find(item => item.id === draftId);
    if (!draft) return;
    // The user's own picture replaces a generated one that has not started yet
    jobs.cancel(draftId);
    setCardBusy(draft.id, 'Uploading...');
    setCardError(draft.id, null);
    try {
      const { filename, size } = await processAndUploadImage(picture, `studio_${draft.id}.jpg`, { autoReduce: true });
      if (draft.image && draft.image !== filename) {
        deleteImage(draft.image).catch(err => console.warn('Could not delete the replaced picture:', err));
      }
      updateQueue(current => current.map(item => {
        if (item.id !== draft.id) return item;
        const updated: DraftLevel = {
          ...item,
          image: filename,
          imageSize: size,
          // A picture the user chose replaces any warnings about the generated one
          flags: item.flags.filter(flag => flag.indexOf(PICTURE_FLAG_PREFIX) !== 0),
          status: item.status === 'needs-picture' ? 'ready' : item.status,
        };
        // The credit belongs to the picture: keep it for a Commons photo, drop it for any other
        if (credit) updated.imageCredit = credit; else delete updated.imageCredit;
        return updated;
      }));
    } catch (err) {
      setCardError(draft.id, describeAIError(err));
    } finally {
      setCardBusy(draft.id, null);
    }
  };

  const rebuildGrid = (draft: DraftLevel) => {
    const grid = buildGrid(draft.words || []);
    if (!grid) {
      setCardError(draft.id, 'These words do not fit together in an 8×8 grid. Click Edit to change them.');
      return;
    }
    setCardError(draft.id, null);
    updateQueue(current => current.map(item => item.id === draft.id
      ? { ...item, wordSearchGrid: grid, status: item.status === 'needs-grid' ? 'ready' : item.status }
      : item));
  };

  const editDraft = (draft: DraftLevel) => {
    jobs.cancel(draft.id);
    factJobs.cancel(draft.id);
    onEditDraft(draft, draftToPrefill(draft, nextFreeId(levels)));
  };

  const flaggedReady = visibleQueue.filter(draft => canApprove(draft) && !canBulkApprove(draft)).length;
  const picturesInProgress = jobsState.running !== null || jobsState.waiting.length > 0;
  const factsInProgress = factJobsState.running !== null || factJobsState.waiting.length > 0;
  const waitingJobs = jobsState.waiting.length + factJobsState.waiting.length;

  return (
    <div className="studio">
      <h2>Level Studio</h2>

      <OrderPanel
        levels={levels}
        counts={counts}
        theme={theme}
        running={running}
        hasClaudeKey={hasClaudeKey}
        hasGeminiKey={hasGeminiKey}
        withFacts={withFacts}
        onWithFacts={setWithFacts}
        onFill={(total) => setCounts(fillGaps(levels, loadQueue(), total))}
        onCount={(type, count) => setCounts(prev => ({ ...prev, [type]: count }))}
        onTheme={setTheme}
        onStart={start}
      />

      {error && <div className="studio-error">{error}</div>}
      {notice && (
        <div className="studio-notice">
          {notice}
          {dropped.length > 0 && (
            <ul className="studio-dropped">
              {dropped.map((item, index) => <li key={index}>Skipped "{item.label}": {item.reason}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="studio-panel">
        <div className="studio-queue-header">
          <h3 style={{ margin: 0 }}>Review queue ({queue.length})</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {picturesInProgress && (
              <span className="studio-hint">
                Making a picture{jobsState.waiting.length > 0 ? `, ${jobsState.waiting.length} waiting` : ''}
              </span>
            )}
            {factsInProgress && (
              <span className="studio-hint">
                Writing a fun fact{factJobsState.waiting.length > 0 ? `, ${factJobsState.waiting.length} waiting` : ''}
              </span>
            )}
            {waitingJobs > 0 && (
              <button className="btn btn-secondary btn-small" onClick={() => { jobs.cancelWaiting(); factJobs.cancelWaiting(); }}>
                <Square size={14} /> Cancel the {waitingJobs} waiting
              </button>
            )}
            {missingFacts.length > 0 && hasClaudeKey && (
              <button
                className="btn btn-secondary btn-small"
                onClick={() => missingFacts.forEach(draft => writeFact(draft.id))}
                title="Finds and checks a fun fact for every level shown that has none"
              >
                <Wand2 size={14} /> Add missing fun facts ({missingFacts.length})
              </button>
            )}
            <button className="btn btn-primary btn-small" onClick={approveAll} disabled={bulkReady.length === 0}>
              <CheckCheck size={14} /> Approve all ready ({bulkReady.length})
            </button>
          </div>
        </div>

        {queue.length > 1 && (
          <div className="studio-queue-tools">
            <div className="studio-filter">
              <button
                className={`studio-chip${activeFilter === 'all' ? ' active' : ''}`}
                onClick={() => setTypeFilter('all')}
              >
                All ({queue.length})
              </button>
              {ORDERED_TYPES.filter(type => typeCounts[type] > 0).map(type => (
                <button
                  key={type}
                  className={`studio-chip${activeFilter === type ? ' active' : ''}`}
                  onClick={() => setTypeFilter(type)}
                >
                  {LEVEL_TYPE_LABELS[type]} ({typeCounts[type]})
                </button>
              ))}
            </div>
            <div className="studio-order">
              <span className="studio-hint">Order:</span>
              <button className="btn btn-secondary btn-small" onClick={() => updateQueue(current => mixTypes(current))} title="Shuffle so the same type does not come twice in a row">
                <Shuffle size={14} /> Mix types
              </button>
              <button className="btn btn-secondary btn-small" onClick={() => updateQueue(groupByType)} title="Put all levels of each type together">
                <Layers size={14} /> Group by type
              </button>
              <button className="btn btn-secondary btn-small" onClick={() => updateQueue(newestFirst)} title="Most recently generated first">
                <Clock size={14} /> Newest first
              </button>
            </div>
          </div>
        )}

        {queue.length > 1 && (
          <p className="studio-hint" style={{ marginTop: '0.5rem' }}>
            "Approve all ready" saves levels in the order shown, so this is the order players will meet them.
            {activeFilter !== 'all' ? ' With a type selected, it approves only that type.' : ''}
          </p>
        )}

        {flaggedReady > 0 && (
          <p className="studio-hint" style={{ marginTop: '0.5rem' }}>
            {flaggedReady} ready level{flaggedReady === 1 ? ' has' : 's have'} a warning and {flaggedReady === 1 ? 'is' : 'are'} left out of "Approve all". Check {flaggedReady === 1 ? 'it' : 'them'} and approve on the card.
          </p>
        )}

        {queue.length === 0 ? (
          <p className="studio-hint" style={{ marginTop: '0.75rem' }}>
            No levels waiting. Choose how many of each type you want above and generate a batch.
          </p>
        ) : (
          <div className="studio-queue">
            {visibleQueue.map(draft => (
              <DraftCard
                key={draft.id}
                draft={draft}
                busy={busy[draft.id] || null}
                waiting={jobsState.waiting.indexOf(draft.id) !== -1}
                error={cardErrors[draft.id] || null}
                onApprove={() => { jobs.cancel(draft.id); factJobs.cancel(draft.id); approve(draft.id); }}
                factState={jobStateOf(factJobsState, draft.id)}
                factError={factErrors[draft.id] || null}
                onWriteFact={() => writeFact(draft.id)}
                onCancelFact={() => factJobs.cancel(draft.id)}
                onRemoveFact={() => removeFact(draft.id)}
                onEdit={() => editDraft(draft)}
                onReject={() => reject(draft)}
                onPicture={(file) => setCropping({ draftId: draft.id, file, credit: null })}
                onFindPicture={() => setFinding({ draftId: draft.id, query: searchQueryFor(draft) })}
                onGeneratePicture={() => generatePicture(draft.id)}
                onCancelPicture={() => jobs.cancel(draft.id)}
                onRebuildGrid={() => rebuildGrid(draft)}
              />
            ))}
          </div>
        )}
      </div>

      {finding && (
        <ImageSearchModal
          initialQuery={finding.query}
          onCancel={() => setFinding(null)}
          onPick={(image, credit) => {
            const { draftId } = finding;
            setFinding(null);
            setCropping({ draftId, file: image, credit });
          }}
        />
      )}

      {cropping && (
        <ImageCropModal
          file={cropping.file}
          onCancel={() => setCropping(null)}
          onConfirm={(cropped) => {
            const { draftId, credit } = cropping;
            setCropping(null);
            attachPicture(draftId, cropped, credit);
          }}
        />
      )}
    </div>
  );
};

export default LevelStudio;
